import {
  Table, Badge, Group, Button, Modal, TextInput, ActionIcon, Tooltip,
  Alert, Skeleton, Text, Stack, MultiSelect, ScrollArea,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconTrash, IconUsers, IconAlertTriangle } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ldapApi, type LdapGroup } from '@/api/ldap'
import { formatApiError } from '@/api/errors'

export function GruposTab() {
  const qc = useQueryClient()
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false)
  const [membersGroup, setMembersGroup] = useState<LdapGroup | null>(null)
  const [addMembersValue, setAddMembersValue] = useState<string[]>([])

  // Create form
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['ldap-groups'],
    queryFn: () => ldapApi.listGroups(false),
  })

  const { data: persons } = useQuery({
    queryKey: ['ldap-persons'],
    queryFn: () => ldapApi.listPersons(),
  })

  const createMutation = useMutation({
    mutationFn: () => ldapApi.createGroup({ name: newName, description: newDescription }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ldap-groups'] })
      closeCreate()
      setNewName('')
      setNewDescription('')
      notifications.show({ color: 'teal', title: 'Grupo criado', message: `${newName} criado com sucesso.` })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao criar grupo.') })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (name: string) => ldapApi.deleteGroup(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ldap-groups'] })
      notifications.show({ color: 'red', title: 'Grupo removido', message: '' })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao remover grupo.') })
    },
  })

  const addMembersMutation = useMutation({
    mutationFn: ({ group, usernames }: { group: string; usernames: string[] }) =>
      ldapApi.addMembers(group, usernames),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ldap-groups'] })
      setAddMembersValue([])
      notifications.show({ color: 'teal', title: 'Membros adicionados', message: '' })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao adicionar membros.') })
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: ({ group, username }: { group: string; username: string }) =>
      ldapApi.removeMember(group, username),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ldap-groups'] })
      notifications.show({ color: 'orange', title: 'Membro removido', message: '' })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao remover membro.') })
    },
  })

  if (isLoading) return <Skeleton height={200} />
  if (error) return <Alert color="red" icon={<IconAlertTriangle />}>Falha ao carregar grupos do Kanidm.</Alert>

  const groups = data?.items ?? []
  const personOptions = (persons?.items ?? []).map((p) => ({
    value: p.username,
    label: `${p.username} (${p.email})`,
  }))

  return (
    <>
      <Group justify="flex-end" mb="sm">
        <Button leftSection={<IconPlus size={14} />} size="xs" onClick={openCreate}>
          Novo Grupo
        </Button>
      </Group>

      <ScrollArea>
        <Table striped highlightOnHover withTableBorder fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nome</Table.Th>
              <Table.Th>Descrição</Table.Th>
              <Table.Th>Membros</Table.Th>
              <Table.Th>Tipo</Table.Th>
              <Table.Th>Ações</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groups.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center" size="sm">Nenhum grupo encontrado.</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {groups.map((g) => (
              <Table.Tr key={g.name}>
                <Table.Td>
                  <Text size="sm" fw={500}>{g.name}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">{g.description || '—'}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="outline">{g.member_count}</Badge>
                </Table.Td>
                <Table.Td>
                  {g.is_system ? (
                    <Badge size="xs" color="violet">Sistema</Badge>
                  ) : (
                    <Badge size="xs" color="gray">Custom</Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <Tooltip label="Ver/Gerenciar membros">
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        color="blue"
                        onClick={() => setMembersGroup(g)}
                      >
                        <IconUsers size={13} />
                      </ActionIcon>
                    </Tooltip>
                    {!g.is_system && (
                      <Tooltip label="Deletar grupo">
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          color="red"
                          onClick={() => {
                            if (confirm(`Deletar grupo ${g.name}?`))
                              deleteMutation.mutate(g.name)
                          }}
                        >
                          <IconTrash size={13} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      {/* Modal: Criar grupo */}
      <Modal opened={createOpened} onClose={closeCreate} title="Novo Grupo no Kanidm" size="sm">
        <Stack gap="sm">
          <TextInput
            label="Nome"
            placeholder="devops-team"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
          />
          <TextInput
            label="Descrição"
            placeholder="Grupo de DevOps"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeCreate}>Cancelar</Button>
            <Button
              onClick={() => createMutation.mutate()}
              loading={createMutation.isPending}
              disabled={!newName}
            >
              Criar
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal: Membros do grupo */}
      <Modal
        opened={!!membersGroup}
        onClose={() => { setMembersGroup(null); setAddMembersValue([]) }}
        title={`Membros: ${membersGroup?.name}`}
        size="md"
      >
        {membersGroup && (
          <Stack gap="sm">
            <Text size="sm" fw={500}>Membros atuais:</Text>
            {membersGroup.members.length === 0 ? (
              <Text c="dimmed" size="sm">Nenhum membro.</Text>
            ) : (
              membersGroup.members.map((m) => (
                <Group key={m} justify="space-between">
                  <Text size="sm">{m}</Text>
                  {!membersGroup.is_system && (
                    <ActionIcon
                      size="xs"
                      color="red"
                      variant="subtle"
                      onClick={() =>
                        removeMemberMutation.mutate({
                          group: membersGroup.name,
                          username: m.split('@')[0].replace('spn=', ''),
                        })
                      }
                    >
                      <IconTrash size={12} />
                    </ActionIcon>
                  )}
                </Group>
              ))
            )}

            {!membersGroup.is_system && (
              <>
                <Text size="sm" fw={500} mt="xs">Adicionar membros:</Text>
                <MultiSelect
                  size="xs"
                  data={personOptions}
                  value={addMembersValue}
                  onChange={setAddMembersValue}
                  placeholder="Selecionar usuários"
                  searchable
                />
                <Button
                  size="xs"
                  disabled={addMembersValue.length === 0}
                  loading={addMembersMutation.isPending}
                  onClick={() =>
                    addMembersMutation.mutate({ group: membersGroup.name, usernames: addMembersValue })
                  }
                >
                  Adicionar
                </Button>
              </>
            )}
          </Stack>
        )}
      </Modal>
    </>
  )
}
